import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, Snowflake, ExternalLink } from "lucide-react";

export default function BioPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    publicApi.get(`/public/bio/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]" data-testid="bio-page-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8] px-6">
        <p className="text-[#71717A]" data-testid="bio-page-not-found">Esta página no está disponible.</p>
      </div>
    );
  }

  const company = data.company || {};
  const mainPhone = company.phones?.[0];

  return (
    <div className="min-h-screen bg-[#F7F7F8] px-4 py-8" data-testid="public-bio-page">
      <div className="max-w-md mx-auto">
        <div className="flex flex-col items-center text-center mb-6">
          {company.logo_url ? (
            <img src={company.logo_url} alt={company.name} className="h-16 w-16 rounded-full object-cover" data-testid="bio-page-logo" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-[#0055FF] grid place-items-center">
              <Snowflake className="h-7 w-7 text-white" />
            </div>
          )}
          <p className="font-heading font-bold text-lg text-[#09090B] mt-3" data-testid="bio-page-company-name">{company.name}</p>
        </div>

        <div className="space-y-3">
          {mainPhone && (
            <a href={`tel:${mainPhone}`} data-testid="bio-page-call-button" className="block">
              <Button className="w-full bg-[#0055FF] hover:bg-[#0044CC] rounded-md h-12 text-base">
                <Phone className="h-4 w-4 mr-2" /> Llamar {mainPhone}
              </Button>
            </a>
          )}
          <a href={data.lead_form_url} data-testid="bio-page-quote-button" className="block">
            <Button variant="outline" className="w-full rounded-md h-12 text-base border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/5">
              Pedir un presupuesto gratis
            </Button>
          </a>
        </div>

        {data.publications?.length > 0 && (
          <div className="mt-8">
            <p className="text-xs uppercase tracking-wider text-[#71717A] mb-3">Últimas publicaciones</p>
            <div className="space-y-3" data-testid="bio-page-publications">
              {data.publications.map((p, i) => (
                <div key={i} className="bg-white rounded-md border border-[#E4E4E7] p-3 flex gap-3" data-testid={`bio-page-publication-${i}`}>
                  {p.media_url && <img src={p.media_url} alt="" className="h-14 w-14 rounded-sm object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#27272A] line-clamp-2">{p.text}</p>
                    {p.post_url && (
                      <a href={p.post_url} target="_blank" rel="noreferrer" className="text-xs text-[#0055FF] flex items-center gap-1 mt-1">
                        Ver publicación <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
